/**
 * WB Agent API client — thin wrapper around the wb_agent HTTP routes.
 *
 * Auth is handled the same way as LlmProxyClient: Basic auth header with
 * entity/project passed as query or body params, credentials: 'include'
 * for cookie-based sessions.
 */

import {getCookie} from '@wandb/weave/common/util/cookie';
import {urlPrefixed} from '@wandb/weave/config';
import type {components, operations, paths} from './generated/wb-agent-types';

// =============================================================================
// Types — generated from services/wb_agent/openapi.json
// =============================================================================

export type TurnState = components['schemas']['TurnState'];

export const TERMINAL_STATES: TurnState[] = [
  'completed',
  'errored',
  'cancelled',
];

export type MessageRecord = components['schemas']['MessageRecord'];
export type ToolCall = components['schemas']['ToolCall'];
export type ToolCallInvocationRecord =
  components['schemas']['ToolCallInvocationRecord'];
export type ToolCallResponseRecord =
  components['schemas']['ToolCallResponseRecord'];
export type ToolCallRecord = NonNullable<
  components['schemas']['TurnResponse']['tool_calls']
>[number];
export type TurnResponse = components['schemas']['TurnResponse'];
export type CreateTurnRequest = components['schemas']['CreateTurnRequest'];
export type ThreadResponse = components['schemas']['ThreadResponse'];
export type UpdateThreadRequest = components['schemas']['UpdateThreadRequest'];
export type GeneratedThreadTitleResponse =
  components['schemas']['GeneratedThreadTitleResponse'];
export type SetFeedbackRequest = components['schemas']['SetFeedbackRequest'];
export type IsAvailableResponse = components['schemas']['IsAvailableResponse'];
export type {
  AgentQuestion,
  MultipleChoiceQuestion,
} from './agentQuestions';
export type {
  NetworkAccessRequest,
  TurnPermissions,
  TurnPermissionsPatch,
} from './networkAccess';

// =============================================================================
// Route-level type safety — auto-derived from the generated paths/operations
// =============================================================================

/** Extract the first 2xx JSON response type from an operation. */
type SuccessResponseOf<R> = R extends {
  200: {content: {'application/json': infer T}};
}
  ? T
  : R extends {201: {content: {'application/json': infer T}}}
    ? T
    : R extends {202: {content: {'application/json': infer T}}}
      ? T
      : never;
type SuccessResponse<Op> = Op extends {responses: infer R}
  ? SuccessResponseOf<R>
  : never;

/**
 * Query params for an operation. Includes optional `entity` and `project`
 * keys so callers can override the client's constructor scope per call.
 */
type QueryParams<Op> = Op extends {parameters: {query?: infer Q}}
  ? NonNullable<Q>
  : Record<string, never>;

/** JSON request body type. */
type RequestBody<Op> = Op extends {
  requestBody: {content: {'application/json': infer B}};
}
  ? B
  : never;

/** Strip the /api/v1 prefix that the client prepends automatically. */
type StripApiPrefix<P extends string> = P extends `/api/v1${infer Rest}`
  ? Rest
  : never;

/** True when a path contains {param} segments (dynamic routes). */
type HasPathParams<P extends string> = P extends `${string}{${string}}${string}`
  ? true
  : false;

/**
 * GET route map — auto-derived from paths with a `get` operation and no path
 * parameters.  Maps stripped path → { response, params }.
 */
type GETRoutes = {
  [P in keyof paths & string as HasPathParams<P> extends true
    ? never
    : paths[P] extends {get: unknown}
      ? StripApiPrefix<P>
      : never]: paths[P] extends {get: infer Op}
    ? {response: SuccessResponse<Op>; params: QueryParams<Op>}
    : never;
};

/**
 * POST route map — auto-derived from paths with a `post` operation and no
 * path parameters.  Maps stripped path → { response, body, params }.
 */
type POSTRoutes = {
  [P in keyof paths & string as HasPathParams<P> extends true
    ? never
    : paths[P] extends {post: unknown}
      ? StripApiPrefix<P>
      : never]: paths[P] extends {post: infer Op}
    ? {
        response: SuccessResponse<Op>;
        body: RequestBody<Op>;
        params: QueryParams<Op>;
      }
    : never;
};

/**
 * PATCH route map. Auto-derived from paths with a `patch` operation and no
 * path parameters.  Maps stripped path → { response, body, params }.
 */
type PATCHRoutes = {
  [P in keyof paths & string as HasPathParams<P> extends true
    ? never
    : paths[P] extends {patch: unknown}
      ? StripApiPrefix<P>
      : never]: paths[P] extends {patch: infer Op}
    ? {
        response: SuccessResponse<Op>;
        body: RequestBody<Op>;
        params: QueryParams<Op>;
      }
    : never;
};

/**
 * Query parameters accepted by GET /turns. `entity` and `project` default to
 * the client's constructor scope; callers may override per call (pass
 * `undefined` to opt out of scoping).
 */
export type QueryTurnsParams = QueryParams<
  operations['query_turns_api_v1_turns_get']
>;

// =============================================================================
// Client
// =============================================================================

export interface WbAgentClientConfig {
  baseUrl: string; // e.g. "http://localhost:8090"
  entity?: string;
  project?: string;
}

export class WbAgentClient {
  private readonly baseUrl: string;
  readonly entity: string | undefined;
  readonly project: string | undefined;

  constructor(config: WbAgentClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.entity = config.entity;
    this.project = config.project;
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  private buildHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      Authorization: 'Basic ' + btoa(':'),
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (getCookie('use_admin_privileges') === 'true') {
      headers['use-admin-privileges'] = 'true';
    }
    return headers;
  }

  /**
   * scopedUrl builds an `/api/v1` URL with query params. Caller params
   * override constructor scope; pass `undefined` to opt out.
   */
  private scopedUrl(
    path: string,
    params: Record<string, unknown> = {}
  ): string {
    const merged = {entity: this.entity, project: this.project, ...params};

    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(merged)) {
      if (value != null) qs.set(key, String(value));
    }

    const query = qs.toString();
    return `${this.baseUrl}/api/v1${path}${query && `?${query}`}`;
  }

  private async request<T>(
    method: string,
    url: string,
    body?: unknown,
    signal?: AbortSignal
  ): Promise<T> {
    // oxlint-disable-next-line wandb/no-unprefixed-urls
    const response = await fetch(urlPrefixed(url), {
      method,
      credentials: 'include',
      headers: this.buildHeaders(),
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });

    if (!response.ok) {
      let errorBody: string;
      try {
        errorBody = await response.text();
      } catch {
        errorBody = response.statusText;
      }
      throw new Error(`WbAgent API ${response.status}: ${errorBody}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  // ── Generic HTTP methods ───────────────────────────────────────────

  /** Typed overload — fixed paths, fully inferred response & params. */
  get<P extends keyof GETRoutes & string>(
    path: P,
    params?: GETRoutes[P]['params'],
    signal?: AbortSignal
  ): Promise<GETRoutes[P]['response']>;
  /** Fallback overload — dynamic paths, caller specifies T. */
  get<T>(
    path: string,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T>;
  async get(
    path: string,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<unknown> {
    return this.request('GET', this.scopedUrl(path, params), undefined, signal);
  }

  /** Return raw streaming responses, e.g. text/event-stream SSE. */
  async stream(
    path: string,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<Response> {
    const response = await fetch(urlPrefixed(this.scopedUrl(path, params)), {
      method: 'GET',
      credentials: 'include',
      headers: this.buildHeaders(),
      signal,
    });
    if (!response.ok) {
      throw new Error(
        `WbAgent API ${response.status}: ${await response.text()}`
      );
    }
    return response;
  }

  /** Typed overload — fixed paths, fully inferred response, body & params. */
  post<P extends keyof POSTRoutes & string>(
    path: P,
    body?: POSTRoutes[P]['body'],
    params?: POSTRoutes[P]['params'],
    signal?: AbortSignal
  ): Promise<POSTRoutes[P]['response']>;
  /** Fallback overload — dynamic paths, caller specifies T. */
  post<T>(
    path: string,
    body?: unknown,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T>;
  async post(
    path: string,
    body?: unknown,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<unknown> {
    return this.request('POST', this.scopedUrl(path, params), body, signal);
  }

  /** Typed overload — fixed paths, fully inferred response, body & params. */
  patch<P extends keyof PATCHRoutes & string>(
    path: P,
    body?: PATCHRoutes[P]['body'],
    params?: PATCHRoutes[P]['params'],
    signal?: AbortSignal
  ): Promise<PATCHRoutes[P]['response']>;
  /** Fallback overload — dynamic paths, caller specifies T. */
  patch<T>(
    path: string,
    body?: unknown,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T>;
  async patch(
    path: string,
    body?: unknown,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<unknown> {
    return this.request('PATCH', this.scopedUrl(path, params), body, signal);
  }
}
